/**
 * Kinessia — global.js
 * Vanilla JS only, no framework, loaded with `defer`.
 * Handles: reveal-on-scroll, mobile menu, cart drawer (AJAX), quantity inputs,
 * sticky add-to-cart visibility, header search toggle.
 *
 * i18n: this file is a static asset (no Liquid). Every user-facing string
 * and the active locale come from `window.KinessiaStrings`, written by
 * layout/theme.liquid from locales/*.json, with French fallbacks here.
 */
(() => {
  "use strict";

  const STRINGS = window.KinessiaStrings || {};
  const LOCALE = STRINGS.locale || document.documentElement.lang || "fr";
  const T = {
    noResults: STRINGS.noResults || "Aucun résultat.",
    addToCartError: STRINGS.addToCartError || "Impossible d'ajouter ce produit pour le moment.",
    inStock: STRINGS.inStock || "En stock, expédié sous 24-48h",
    soldOut: STRINGS.soldOut || "Rupture de stock",
    deliveryEstimate: STRINGS.deliveryEstimate || "Livraison estimée entre le __FROM__ et le __TO__",
  };

  function formatCurrency(amount) {
    try {
      return Number(amount).toLocaleString(LOCALE, { style: "currency", currency: "EUR" });
    } catch (err) {
      return Number(amount).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
    }
  }

  function formatLongDate(date) {
    try {
      return date.toLocaleDateString(LOCALE, { day: "numeric", month: "long" });
    } catch (err) {
      return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
    }
  }

  /* ---------------------------------------------------------------------
   * Reveal on scroll
   * ------------------------------------------------------------------- */
  const revealTargets = document.querySelectorAll("[data-reveal]");
  if (revealTargets.length && "IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    revealTargets.forEach((el) => observer.observe(el));
  } else {
    revealTargets.forEach((el) => el.classList.add("is-visible"));
  }

  /* ---------------------------------------------------------------------
   * Hero slideshow — autoplay every 4-5s (configurable via section
   * settings) with manual dots/arrows navigation. Pauses on hover/focus
   * and whenever the slideshow scrolls out of view, and is skipped
   * entirely when the visitor has requested reduced motion.
   * ------------------------------------------------------------------- */
  document.querySelectorAll("[data-slideshow]").forEach((root) => {
    const slides = Array.from(root.querySelectorAll("[data-slide]"));
    if (slides.length < 2) return; // nothing to cycle through

    const dots = Array.from(root.querySelectorAll("[data-slide-dot]"));
    const prevBtn = root.querySelector("[data-slide-prev]");
    const nextBtn = root.querySelector("[data-slide-next]");
    const pauseBtn = root.querySelector("[data-slide-pause]");
    const pauseIcon = root.querySelector("[data-slide-pause-icon]");

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const autoplayEnabled = root.getAttribute("data-autoplay") === "true" && !reducedMotion;
    const interval = parseInt(root.getAttribute("data-interval"), 10) || 5000;

    let current = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains("is-active"))
    );
    let timer = null;
    let userPaused = false;

    function goTo(index) {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle("is-active", i === current));
      dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === current);
        dot.setAttribute("aria-selected", String(i === current));
      });
    }

    function next() {
      goTo(current + 1);
    }
    function prev() {
      goTo(current - 1);
    }

    function stopAutoplay() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }
    function startAutoplay() {
      if (!autoplayEnabled || userPaused) return;
      stopAutoplay();
      timer = setInterval(next, interval);
    }

    dots.forEach((dot, i) => {
      dot.addEventListener("click", () => {
        goTo(i);
        startAutoplay();
      });
    });
    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        prev();
        startAutoplay();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        next();
        startAutoplay();
      });
    }

    if (pauseBtn) {
      pauseBtn.addEventListener("click", () => {
        userPaused = !userPaused;
        pauseBtn.setAttribute("aria-pressed", String(userPaused));
        if (pauseIcon) pauseIcon.textContent = userPaused ? "►" : "❚❚";
        if (userPaused) stopAutoplay();
        else startAutoplay();
      });
    }

    // Accessible pause: hovering or focusing anything inside the slideshow
    // stops the rotation so a slide doesn't change under the cursor/reader.
    root.addEventListener("mouseenter", stopAutoplay);
    root.addEventListener("mouseleave", () => {
      if (!userPaused) startAutoplay();
    });
    root.addEventListener("focusin", stopAutoplay);
    root.addEventListener("focusout", (event) => {
      if (!root.contains(event.relatedTarget) && !userPaused) startAutoplay();
    });

    // Also pause when scrolled out of view (saves cycles, avoids a jump
    // when the visitor scrolls back to a slide they never saw change).
    if ("IntersectionObserver" in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) startAutoplay();
          else stopAutoplay();
        },
        { threshold: 0.25 }
      );
      visibilityObserver.observe(root);
    } else {
      startAutoplay();
    }

    goTo(current);
  });

  /* ---------------------------------------------------------------------
   * Focus trap — reusable for any modal-like overlay (menu, cart, popup).
   * Keeps Tab/Shift+Tab cycling inside the container, closes on Escape,
   * and returns focus to whatever triggered the overlay once it closes.
   * ------------------------------------------------------------------- */
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function createFocusTrap(container, onEscape) {
    let lastFocused = null;
    let isActive = false;

    // Escape is handled on `document` and gated by `isActive` rather than
    // relying on the keydown bubbling up through `container` — that bubbling
    // only works if focus is actually inside the container, which is exactly
    // the part that's fragile (native focus-follows-click on the triggering
    // button can win a race against our own focus() call). Tab-cycling still
    // needs real focus tracking, so it stays container-relative below.
    function handleDocumentKeydown(event) {
      if (isActive && event.key === "Escape") onEscape();
    }

    function handleContainerKeydown(event) {
      if (event.key !== "Tab") return;

      const focusable = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleDocumentKeydown);

    return {
      activate() {
        isActive = true;
        lastFocused = document.activeElement;
        container.addEventListener("keydown", handleContainerKeydown);
        // Deferred to the next tick: the browser's native focus-follows-click
        // behavior on the button that triggered this would otherwise run
        // right after this handler and steal focus back, breaking the trap.
        setTimeout(() => {
          const focusable = container.querySelector(FOCUSABLE_SELECTOR);
          if (focusable) focusable.focus();
        }, 0);
      },
      deactivate() {
        isActive = false;
        container.removeEventListener("keydown", handleContainerKeydown);
        if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
      },
    };
  }

  /* ---------------------------------------------------------------------
   * Mobile menu
   * ------------------------------------------------------------------- */
  const menuPanel = document.querySelector("[data-menu-panel]");
  const menuOverlay = document.querySelector("[data-menu-panel-overlay]");
  if (menuPanel) {
    const menuToggles = document.querySelectorAll("[data-menu-toggle]");

    const menuFocusTrap = createFocusTrap(menuPanel, () => setMenuOpen(false));

    function setMenuOpen(open) {
      menuPanel.toggleAttribute("data-open", open);
      menuPanel.setAttribute("aria-hidden", String(!open));
      menuToggles.forEach((btn) => btn.setAttribute("aria-expanded", String(open)));
      document.body.style.overflow = open ? "hidden" : "";
      if (open) {
        menuFocusTrap.activate();
      } else {
        menuFocusTrap.deactivate();
      }
    }

    menuToggles.forEach((btn) => {
      btn.addEventListener("click", () => setMenuOpen(!menuPanel.hasAttribute("data-open")));
    });

    if (menuOverlay) {
      menuOverlay.addEventListener("click", () => setMenuOpen(false));
    }
  }

  /* ---------------------------------------------------------------------
   * Header dropdown / mega-menu — hover-intent with a closing grace
   * period so crossing a small gap between the trigger and the panel
   * doesn't slam it shut, plus Escape-to-close for keyboard users.
   * CSS still opens the panel on :focus-within as a no-JS fallback;
   * this layer replaces :hover so the delay actually has an effect
   * (a pure-CSS :hover would close instantly on mouseleave regardless).
   * Not a focus trap on purpose: it's a disclosure panel, not a modal,
   * so Tab should be free to keep moving through the page.
   * ------------------------------------------------------------------- */
  const dropdownItems = document.querySelectorAll(
    ".header__nav-item.has-dropdown, .header__nav-item.has-mega-menu"
  );
  if (dropdownItems.length) {
    const CLOSE_DELAY = 250; // ms grace period before closing on mouseleave
    const closeTimers = new WeakMap();

    function openDropdown(item) {
      dropdownItems.forEach((other) => {
        if (other !== item) other.removeAttribute("data-open");
      });
      item.setAttribute("data-open", "");
    }
    function closeDropdown(item) {
      item.removeAttribute("data-open");
    }
    function cancelScheduledClose(item) {
      const existing = closeTimers.get(item);
      if (existing) clearTimeout(existing);
    }
    function scheduleClose(item) {
      cancelScheduledClose(item);
      closeTimers.set(
        item,
        setTimeout(() => closeDropdown(item), CLOSE_DELAY)
      );
    }

    dropdownItems.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        cancelScheduledClose(item);
        openDropdown(item);
      });
      item.addEventListener("mouseleave", () => scheduleClose(item));
      item.addEventListener("focusin", () => {
        cancelScheduledClose(item);
        openDropdown(item);
      });
      item.addEventListener("focusout", (event) => {
        if (!item.contains(event.relatedTarget)) scheduleClose(item);
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const openItem = document.querySelector(
        ".header__nav-item.has-dropdown[data-open], .header__nav-item.has-mega-menu[data-open]"
      );
      if (!openItem) return;
      closeDropdown(openItem);
      const trigger = openItem.querySelector(":scope > a");
      if (trigger) trigger.focus();
    });
  }

  /* ---------------------------------------------------------------------
   * Header search toggle
   * ------------------------------------------------------------------- */
  const searchToggle = document.querySelector("[data-search-toggle]");
  const searchPanel = document.querySelector("[data-search-panel]");
  if (searchToggle && searchPanel) {
    searchToggle.addEventListener("click", () => {
      const isOpen = searchPanel.hasAttribute("data-open");
      searchPanel.toggleAttribute("data-open", !isOpen);
      searchToggle.setAttribute("aria-expanded", String(!isOpen));
      if (!isOpen) {
        const input = searchPanel.querySelector("input[type='search']");
        if (input) input.focus();
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Predictive search — native /search/suggest.json + Section Rendering API
   * ------------------------------------------------------------------- */
  const predictiveInput = document.querySelector("[data-predictive-search-input]");
  const predictiveResults = document.querySelector("[data-predictive-search-results]");
  if (predictiveInput && predictiveResults) {
    let debounceTimer;
    let currentController;

    const clearResults = () => {
      predictiveResults.innerHTML = "";
      predictiveResults.hidden = true;
      predictiveInput.setAttribute("aria-expanded", "false");
    };

    const escapeHtml = (value) =>
      String(value).replace(/[&<>\"']/g, (char) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
      }[char]));

    const formatPrice = (decimalString) => formatCurrency(decimalString);

    const renderPredictiveResults = (products) => {
      predictiveInput.setAttribute("aria-expanded", "true");

      if (products.length === 0) {
        predictiveResults.innerHTML = '<p class="predictive-search__empty">' + escapeHtml(T.noResults) + "</p>";
        predictiveResults.hidden = false;
        return;
      }

      const items = products.map((product) => {
        const image = product.featured_image && product.featured_image.url
          ? `<img src="${escapeHtml(product.featured_image.url)}" alt="${escapeHtml(product.title)}" width="48" height="48" loading="lazy">`
          : "";
        return `<li class="predictive-search__item" role="option">
          <a href="${escapeHtml(product.url)}">
            ${image}
            <span>
              <span class="predictive-search__item-title">${escapeHtml(product.title)}</span>
              <span class="predictive-search__item-price">${formatPrice(product.price_min)}</span>
            </span>
          </a>
        </li>`;
      }).join("");

      predictiveResults.innerHTML = `<ul class="predictive-search__list" role="presentation">${items}</ul>`;
      predictiveResults.hidden = false;
    };

    predictiveInput.addEventListener("input", () => {
      const term = predictiveInput.value.trim();
      clearTimeout(debounceTimer);

      if (term.length < 2) {
        clearResults();
        return;
      }

      debounceTimer = setTimeout(() => {
        if (currentController) currentController.abort();
        currentController = new AbortController();

        // routes.root already carries the language prefix (e.g. /en/), so
        // suggestions come back in the visitor's language.
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const url =
          root + "search/suggest.json?q=" + encodeURIComponent(term) +
          "&resources[type]=product" +
          "&resources[limit]=6" +
          "&resources[options][unavailable_products]=last";

        fetch(url, { signal: currentController.signal })
          .then((response) => response.json())
          .then((data) => {
            const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
            renderPredictiveResults(products);
          })
          .catch((error) => {
            if (error.name !== "AbortError") clearResults();
          });
      }, 200);
    });

    document.addEventListener("click", (event) => {
      if (!predictiveResults.contains(event.target) && event.target !== predictiveInput) {
        clearResults();
      }
    });

    predictiveInput.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !predictiveResults.hidden) {
        event.stopPropagation();
        clearResults();
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Collection filters — mobile show/hide toggle for the facet sidebar
   * ------------------------------------------------------------------- */
  const filterToggle = document.querySelector("[data-filter-toggle]");
  const filterPanel = document.querySelector("[data-filter-panel]");
  if (filterToggle && filterPanel) {
    filterToggle.addEventListener("click", () => {
      const isOpen = filterPanel.hasAttribute("data-open");
      filterPanel.toggleAttribute("data-open", !isOpen);
      filterToggle.setAttribute("aria-expanded", String(!isOpen));
    });
  }

  // Checking/unchecking a facet submits the form immediately (no extra click).
  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-filter-checkbox]")) {
      event.target.closest("form")?.requestSubmit();
    }
  });

  /* ---------------------------------------------------------------------
   * Quantity selectors (product page, cart, bundle)
   * ------------------------------------------------------------------- */
  document.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-quantity-step]");
    if (!btn) return;
    const wrapper = btn.closest("[data-quantity-wrapper]");
    const input = wrapper && wrapper.querySelector("input[type='number']");
    if (!input) return;
    const step = parseInt(btn.getAttribute("data-quantity-step"), 10);
    const min = parseInt(input.min || "1", 10);
    const newValue = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.value = newValue;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------------------------------------------------------------------
   * Cart drawer — AJAX add to cart + render
   * ------------------------------------------------------------------- */
  const cartDrawer = document.querySelector("[data-cart-drawer]");
  const cartOverlay = document.querySelector("[data-cart-overlay]");

  const cartFocusTrap = cartDrawer ? createFocusTrap(cartDrawer, () => closeCartDrawer()) : null;

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.setAttribute("data-open", "");
    cartOverlay && cartOverlay.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    cartDrawer.setAttribute("aria-hidden", "false");
    if (cartFocusTrap) cartFocusTrap.activate();
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.removeAttribute("data-open");
    cartOverlay && cartOverlay.removeAttribute("data-open");
    document.body.style.overflow = "";
    cartDrawer.setAttribute("aria-hidden", "true");
    if (cartFocusTrap) cartFocusTrap.deactivate();
  }

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-cart-open]")) {
      event.preventDefault();
      openCartDrawer();
    }
    if (event.target.closest("[data-cart-close]") || event.target === cartOverlay) {
      closeCartDrawer();
    }
  });

  // Fenêtre de livraison estimée — calculée côté client : aujourd'hui + 7 à
  // + 10 jours ouvrés (24-48 h de préparation + 6-8 jours ouvrés de
  // transport, comme la politique d'expédition), sans compte à rebours ni
  // fausse urgence. Phrase et langue traduites via KinessiaStrings.
  // Ré-appliquée après chaque refresh du tiroir puisque l'encart est
  // re-rendu par la section.
  function addBusinessDays(days) {
    const date = new Date();
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return date;
  }

  function updateDeliveryEstimate() {
    const slot = document.querySelector("[data-delivery-estimate]");
    if (!slot) return;
    const textEl = slot.querySelector("[data-delivery-estimate-text]");
    if (!textEl) return;
    textEl.textContent = T.deliveryEstimate
      .replace("__FROM__", formatLongDate(addBusinessDays(7)))
      .replace("__TO__", formatLongDate(addBusinessDays(10)));
    slot.hidden = false;
  }
  updateDeliveryEstimate();

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newContent = doc.querySelector("[data-cart-drawer-content]");
      const currentContent = cartDrawer.querySelector("[data-cart-drawer-content]");
      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
      updateDeliveryEstimate();
      // Le compteur du header vit hors de la section cart-drawer : on lit
      // directement le panier via l'API AJAX plutôt que de chercher un
      // élément absent de la réponse section_id=cart-drawer.
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      const bubble = document.querySelector("[data-cart-count]");
      if (bubble) bubble.textContent = cart.item_count;
    } catch (err) {
      console.error("Kinessia: cart refresh failed", err);
    }
  }

  // Intercept product-form submissions site-wide and add via AJAX.
  document.addEventListener("submit", async (event) => {
    const form = event.target.closest("form[data-product-form]");
    if (!form) return;
    event.preventDefault();

    const submitBtn = form.querySelector("[type='submit']");
    if (submitBtn) submitBtn.classList.add("btn--loading");

    try {
      const formData = new FormData(form);
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || T.addToCartError);
      }

      await refreshCartDrawer();
      openCartDrawer();
    } catch (err) {
      console.error("Kinessia: add to cart failed", err);
      const errorSlot = form.querySelector("[data-form-error]");
      if (errorSlot) {
        errorSlot.textContent = T.addToCartError;
        errorSlot.hidden = false;
      }
    } finally {
      if (submitBtn) submitBtn.classList.remove("btn--loading");
    }
  });

  // Quantity / remove updates inside the cart drawer (event delegation).
  document.addEventListener("click", async (event) => {
    const removeBtn = event.target.closest("[data-cart-remove]");
    if (!removeBtn) return;
    event.preventDefault();
    const key = removeBtn.getAttribute("data-cart-remove");
    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    await refreshCartDrawer();
  });

  document.addEventListener("change", async (event) => {
    const qtyInput = event.target.closest("[data-cart-line-quantity]");
    if (!qtyInput) return;
    const key = qtyInput.getAttribute("data-cart-line-quantity");
    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: parseInt(qtyInput.value, 10) || 0 }),
    });
    await refreshCartDrawer();
  });

  // Upsell "Améliorez votre routine" dans le tiroir — même flux cart/add.js
  // que le quick add, mais sans ré-ouvrir ni re-piéger le focus d'un tiroir
  // déjà ouvert : on rafraîchit simplement le contenu.
  document.addEventListener("click", async (event) => {
    const upsellBtn = event.target.closest("[data-upsell-add]");
    if (!upsellBtn) return;
    event.preventDefault();
    upsellBtn.classList.add("btn--loading");
    upsellBtn.disabled = true;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: parseInt(upsellBtn.getAttribute("data-variant-id"), 10), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error("upsell add failed");
      await refreshCartDrawer();
    } catch (err) {
      console.error("Kinessia: upsell add to cart failed", err);
      upsellBtn.classList.remove("btn--loading");
      upsellBtn.disabled = false;
    }
  });

  /* ---------------------------------------------------------------------
   * Product gallery — thumbnail click swaps the active main image
   * ------------------------------------------------------------------- */
  document.addEventListener("click", (event) => {
    const thumb = event.target.closest("[data-gallery-thumb]");
    if (!thumb) return;
    const gallery = thumb.closest("[data-gallery]");
    if (!gallery) return;
    const index = thumb.getAttribute("data-index");

    gallery.querySelectorAll("[data-gallery-image]").forEach((img) => {
      img.classList.toggle("is-active", img.getAttribute("data-index") === index);
    });
    gallery.querySelectorAll("[data-gallery-thumb]").forEach((t) => {
      t.classList.toggle("is-active", t === thumb);
    });
  });

  /* ---------------------------------------------------------------------
   * Variant picker — client-side matching against embedded variants JSON,
   * no network round-trip needed to update price / availability / hidden id.
   * ------------------------------------------------------------------- */
  document.querySelectorAll("form[data-product-form]").forEach((form) => {
    const jsonScript = form.parentElement.querySelector("[data-product-json]");
    if (!jsonScript) return;

    let variants = [];
    try {
      variants = JSON.parse(jsonScript.textContent);
    } catch (err) {
      console.error("Kinessia: invalid product JSON", err);
      return;
    }

    const selectInputs = form.querySelectorAll("[data-option-selector]");
    const radioInputs = form.querySelectorAll("[data-option-selector-radio]");
    if (!selectInputs.length && !radioInputs.length) return;

    const idInput = form.querySelector("[data-variant-id-input]");
    const submitBtn = form.querySelector("[type='submit']");

    function getSelectedOptions() {
      const byPosition = {};
      selectInputs.forEach((select) => {
        byPosition[select.dataset.optionPosition] = select.value;
      });
      form.querySelectorAll("[data-option-selector-radio]:checked").forEach((radio) => {
        byPosition[radio.dataset.optionPosition] = radio.value;
      });
      return Object.keys(byPosition)
        .sort((a, b) => a - b)
        .map((position) => byPosition[position]);
    }

    function findMatchingVariant() {
      const selected = getSelectedOptions();
      return variants.find((variant) =>
        variant.options.every((opt, i) => opt === selected[i])
      );
    }

    function updateUI(variant) {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;

      const priceWrap = form.querySelector(".product-main__price");
      if (priceWrap) {
        const saleEl = priceWrap.querySelector(".product-main__price--sale");
        const compareEl = priceWrap.querySelector(".product-main__price--compare");
        const target = saleEl || priceWrap.querySelector("span");
        if (target) {
          target.textContent = formatMoney(variant.price);
        }
        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      }

      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.textContent = variant.available
          ? submitBtn.getAttribute("data-label-available") || submitBtn.textContent
          : submitBtn.getAttribute("data-label-sold-out") || submitBtn.textContent;
      }

      const availabilityEl = form.querySelector(".product-main__availability");
      if (availabilityEl) {
        const dot = availabilityEl.querySelector(".dot");
        if (dot) dot.className = variant.available ? "dot dot--in-stock" : "dot dot--out";
        const label = availabilityEl.querySelector("[data-availability-label]") || availabilityEl.lastChild;
        if (label && label.nodeType === Node.TEXT_NODE) {
          label.textContent = variant.available ? " " + T.inStock : " " + T.soldOut;
        }
      }
    }

    function formatMoney(cents) {
      return formatCurrency(cents / 100);
    }

    selectInputs.forEach((select) => {
      select.addEventListener("change", () => updateUI(findMatchingVariant()));
    });
    radioInputs.forEach((radio) => {
      radio.addEventListener("change", () => updateUI(findMatchingVariant()));
    });
  });

  /* ---------------------------------------------------------------------
   * Product bundle — recompute total on toggle, add all checked items
   * to the cart in a single cart/add.js request (native Shopify AJAX API).
   * ------------------------------------------------------------------- */
  function formatEuros(cents) {
    return formatCurrency(cents / 100);
  }

  function updateBundleTotal(bundle) {
    const totalEl = bundle.querySelector("[data-bundle-total]");
    if (!totalEl) return;
    const checked = bundle.querySelectorAll("[data-bundle-variant]:checked");
    const discount = parseFloat(bundle.querySelector("[data-bundle-add]").getAttribute("data-discount") || "0");
    let total = 0;
    checked.forEach((box) => { total += parseInt(box.getAttribute("data-bundle-price"), 10) || 0; });
    total = total - (total * discount) / 100;
    totalEl.textContent = formatEuros(total);
  }

  document.querySelectorAll("[data-bundle]").forEach((bundle) => {
    updateBundleTotal(bundle);
    bundle.addEventListener("change", (event) => {
      if (event.target.matches("[data-bundle-variant]")) updateBundleTotal(bundle);
    });
  });

  document.addEventListener("click", async (event) => {
    const addBtn = event.target.closest("[data-bundle-add]");
    if (!addBtn) return;
    const bundle = addBtn.closest("[data-bundle]");
    const checked = bundle.querySelectorAll("[data-bundle-variant]:checked");
    const items = Array.from(checked).map((box) => ({
      id: parseInt(box.getAttribute("data-bundle-variant"), 10),
      quantity: 1,
    }));
    if (!items.length) return;

    addBtn.classList.add("btn--loading");
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error("bundle add failed");
      await refreshCartDrawer();
      openCartDrawer();
    } catch (err) {
      console.error("Kinessia: bundle add to cart failed", err);
    } finally {
      addBtn.classList.remove("btn--loading");
    }
  });

  /* ---------------------------------------------------------------------
   * Product recommendations — fetched after first paint so it never
   * delays LCP on the product page.
   * ------------------------------------------------------------------- */
  const recommendationsEl = document.querySelector("[data-recommendations]");
  if (recommendationsEl) {
    const url = recommendationsEl.getAttribute("data-url");
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const newSection = doc.querySelector("[data-recommendations]");
        if (newSection) recommendationsEl.innerHTML = newSection.innerHTML;
      })
      .catch((err) => console.error("Kinessia: recommendations failed", err));
  }

  /* ---------------------------------------------------------------------
   * Newsletter popup — shown once per visitor (localStorage), triggered
   * after a configurable delay OR 50% scroll, whichever comes first.
   * Never on arrival, never repeated: kept deliberately non-aggressive.
   * The former discount wheel has been removed: it handed out codes that
   * did not exist in the shop. The only active code (BIENVENUE10) is
   * printed server-side by snippets/newsletter-popup.liquid after signup.
   * ------------------------------------------------------------------- */
  const newsletterPopup = document.querySelector("[data-newsletter-popup]");
  if (newsletterPopup) {
    const overlay = document.querySelector("[data-newsletter-popup-overlay]");
    const STORAGE_KEY = "kinessia:newsletter-popup-shown";
    const alreadyShown = window.localStorage.getItem(STORAGE_KEY);

    const popupFocusTrap = createFocusTrap(newsletterPopup, () => closePopup());

    const showPopup = () => {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
      newsletterPopup.setAttribute("data-open", "");
      overlay && overlay.setAttribute("data-open", "");
      newsletterPopup.setAttribute("aria-hidden", "false");
      window.localStorage.setItem(STORAGE_KEY, "1");
      popupFocusTrap.activate();
    };

    const closePopup = () => {
      newsletterPopup.removeAttribute("data-open");
      overlay && overlay.removeAttribute("data-open");
      newsletterPopup.setAttribute("aria-hidden", "true");
      popupFocusTrap.deactivate();
    };

    if (!alreadyShown) {
      const delaySeconds = parseInt(newsletterPopup.getAttribute("data-delay"), 10) || 8;
      const timer = setTimeout(showPopup, delaySeconds * 1000);

      const onScroll = () => {
        const scrolled = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        if (scrolled > 0.5) {
          clearTimeout(timer);
          showPopup();
          window.removeEventListener("scroll", onScroll);
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-newsletter-popup-close]") || event.target === overlay) {
        closePopup();
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Quick add — product cards for single-variant products only (rendered
   * server-side in snippets/product-card.liquid), same cart/add.js flow.
   * ------------------------------------------------------------------- */
  document.addEventListener("click", async (event) => {
    const quickAddBtn = event.target.closest("[data-quick-add]");
    if (!quickAddBtn) return;
    event.preventDefault();
    quickAddBtn.classList.add("btn--loading");
    try {
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: parseInt(quickAddBtn.getAttribute("data-variant-id"), 10), quantity: 1 }] }),
      });
      await refreshCartDrawer();
      openCartDrawer();
    } catch (err) {
      console.error("Kinessia: quick add failed", err);
    } finally {
      quickAddBtn.classList.remove("btn--loading");
    }
  });

  /* ---------------------------------------------------------------------
   * Recently viewed — records the current product (if any) into
   * localStorage, then renders the list wherever the section is present.
   * ------------------------------------------------------------------- */
  (() => {
    const STORAGE_KEY = "kinessia:recently-viewed";
    const MAX_ITEMS = 6;

    function readHistory() {
      try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || [];
      } catch (err) {
        return [];
      }
    }

    const recordScript = document.querySelector("[data-record-view]");
    if (recordScript) {
      try {
        const current = JSON.parse(recordScript.textContent);
        let history = readHistory().filter((item) => item.handle !== current.handle);
        history.unshift(current);
        history = history.slice(0, MAX_ITEMS);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
      } catch (err) {
        console.error("Kinessia: could not record recently viewed product", err);
      }
    }

    const section = document.querySelector("[data-recently-viewed]");
    if (!section) return;

    const currentHandle = recordScript ? JSON.parse(recordScript.textContent).handle : null;
    const items = readHistory().filter((item) => item.handle !== currentHandle);
    if (!items.length) return;

    const list = section.querySelector("[data-recently-viewed-list]");
    list.innerHTML = items
      .map(
        (item) => `
        <div class="product-card">
          <a href="${item.url}" class="product-card__link">
            <div class="product-card__media">
              ${item.image ? `<img class="product-card__img product-card__img--primary" src="${item.image}" alt="${item.title}" loading="lazy">` : ""}
            </div>
            <div class="product-card__info">
              <h3 class="product-card__title">${item.title}</h3>
              <div class="product-card__price">${formatEuros(item.price)}</div>
            </div>
          </a>
        </div>`
      )
      .join("");
    section.hidden = false;
  })();

  /* ---------------------------------------------------------------------
   * Sticky add-to-cart (mobile) — show once the main buy box scrolls out
   * ------------------------------------------------------------------- */
  const buyBox = document.querySelector("[data-buy-box]");
  const stickyBar = document.querySelector("[data-sticky-atc]");
  if (buyBox && stickyBar && "IntersectionObserver" in window) {
    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        stickyBar.toggleAttribute("data-visible", !entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    stickyObserver.observe(buyBox);
  }

  document.addEventListener("click", async (event) => {
    const btn = event.target.closest("[data-sticky-atc-add]");
    if (!btn) return;
    btn.classList.add("btn--loading");
    try {
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute("data-variant-id"), 10), quantity: 1 }] }),
      });
      await refreshCartDrawer();
      openCartDrawer();
    } catch (err) {
      console.error("Kinessia: sticky add to cart failed", err);
    } finally {
      btn.classList.remove("btn--loading");
    }
  });
})();
